import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FileUploader } from 'ng2-file-upload';
import { environment } from '../../../../environments/environment';
import { ApiUrlConstants } from '../../../common/constants/api.constants';
import { CacheKey } from '../../../shared/constants/cachekey.constants';
import { CacheService } from '../../../shared/services/cache.service';
import { CommonService } from '../../../shared/services/common.service';
import { Constants } from '../../common/constants';
import { FileUploadConstants } from '../../constants/file-upload.constants';
import { COLUMN_VALIDATION, DestinationDatabase, FileUploadSource } from '../../enums/file-upload.enum';
import { NotificationType } from '../../enums/notification-types.enum';
import { FileUploadTemplate } from '../../interface/file-uploads/file-upload-template.interface';
import { BatchProcessingService } from '../../services/batch-processing.service';
import { CustomerService } from '../../services/customer.service';
import { saveAs as importedSaveAs } from 'file-saver';

@Component({
    selector: 'app-recognition-upload',
    templateUrl: '../../templates/template1/views/file-uploads/recognition-file-upload.component.html',
    styleUrls: ['../../templates/template1/themes/default/less/recognition-file-upload.less']
})

export class RecognitionFileUploadComponent implements OnInit, OnChanges {
    @Input() templateInfo: FileUploadTemplate;
    @Input() categoryName: string;
    @Input() operatingCountryID: number;
    @Input() categoryIdentifier: string;
    @Input() isValidForm: boolean;
    templateFilePath = '';
    templateHeaders = FileUploadConstants.TEMPLATE_HEADERS;
    levelsInfo = [];
    errorHeaders = [];
    errorRows = [];
    showDownloadErrorFile = false;
    templateId = null;
    uploader: any;
    operatingCountriesLookup = [];
    operatingCountry: string;
    uploadPath: string;
    isFileUploading = false;
    hasErrorColumn = false;
    enableFileUploadToPrivate: Boolean = environment.enableFileUploadToPrivate;

    constructor(private _customerService: CustomerService,
        private _commonService: CommonService,
        private _cacheService: CacheService,
        private _batchService: BatchProcessingService,
        private _translatePipe: TranslateService
    ) { }

    ngOnInit(): void {
        this.uploader = this.getFileUploader();
        this.uploader.onWhenAddingFileFailed =
            (item: any, filter: any, options: any) => this.onWhenAddingFileFailed(item, filter, options);
        this.getOperatingCountries();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.templateInfo && changes.templateInfo.currentValue) {
            this.templateInfo = changes.templateInfo.currentValue;
            this.templateId = this.templateInfo ? this.templateInfo.id : null;
            this.templateFilePath = this.templateInfo ? this.templateInfo.templateFilePath : '';
            this.uploader.queue = [];
            this.showDownloadErrorFile = false;
        }
    }

    /**
     * To Get operating countries
     */
    getOperatingCountries(): void {
        this._customerService.getOperatingCountries().subscribe((response) => {
            if (response && response.length) {
                this.operatingCountriesLookup = response;
            } else {
                this.operatingCountriesLookup = [];
            }
        }, error => {
            this.operatingCountriesLookup = [];
        });
    }

    /**
     * To Get the File Uploader instance
     */
    getFileUploader(): FileUploader {
        const fileUploader = new FileUploader({
            url: ApiUrlConstants.batchApiUrl + '/upload-file',
            //allowedFileType: ['xls']
        });
        return fileUploader;
    }

    /**
     * To Capture the invalid file selection
     */
    onWhenAddingFileFailed(file: any, filter: any, options: any): any {
        this._commonService.removeNotification(Constants.INVALID_FILE_UPLOAD);
        const message: any = this._translatePipe.get('fileuploads.Invalid file format, Please choose valid file format i.e. (.csv)');
        this.uploader.queue = [];
        this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.INVALID_FILE_UPLOAD);
    }

    /**
     * To Select the file by button & drop
     */
    onSelectFile(files, event?): void {
        const file = files && files.length > 0 ? files[0] : null;
        if (this.validCSVFile(file)) {
            if (this.uploader.queue.length && this.uploader.queue.length > 1) {
                this.uploader.queue[0].remove();
            }
            this._commonService.removeNotification(Constants.INVALID_FILE_UPLOAD);
            this.showDownloadErrorFile = false;
        } else {
            this._commonService.removeNotification(Constants.INVALID_FILE_UPLOAD);
            const message: any = this._translatePipe.get('fileuploads.Invalid file format, Please choose valid file format i.e. (.csv)');
            this.uploader.queue = [];
            this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.INVALID_FILE_UPLOAD);
        }
        if (event && event.target && event.target.value) {
            event.target.value = '';
        }
    }

    /**
     * To Download the template
     */
     downloadTemplate(): void {
       if (this.templateFilePath) {
         const url = environment.s3BaseUrl + this.templateFilePath;
         if (this.enableFileUploadToPrivate) {
           let path = new URL(url).pathname.slice(1);
           let s3Path = path.slice(0, path.lastIndexOf("/") + 1);
           let fileName = this.templateFilePath.replace(/^.*[\\\/]/, '');
           this._customerService.downloadFile(s3Path, fileName).subscribe((response) => {
             this._cacheService.set(CacheKey.IsRefreshed, true);
             importedSaveAs(response, fileName);
           }, error => {
             const message: any = this._translatePipe.get('fileuploads.Template you are trying to download was not found, Please try again after some time');
             this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
           });
         } else {
           this._customerService.getTemplatesFromS3(url).subscribe((response) => {
             this._cacheService.set(CacheKey.IsRefreshed, true);
             const a = document.createElement('a');
             a.href = url;
             document.body.appendChild(a);
             a.click();
             document.body.removeChild(a);
           }, error => {
             const message: any = this._translatePipe.get('fileuploads.Template you are trying to download was not found, Please try again after some time');
             this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
           });
         }
       }
    }


    /**
     * To Upload the template
     */
    uploadTemplate(): void {
        this.showDownloadErrorFile = false;
        this._commonService.removeNotification(Constants.FILE_UPLOAD);
        if (this.uploader && this.uploader.queue && this.uploader.queue.length > 0) {
            const fileItem = this.uploader.queue[0];
            const reader = new FileReader();
            const file = fileItem && fileItem.file && fileItem.file.rawFile ? fileItem.file.rawFile : null;
            const isValidCSVFile = this.validCSVFile(file);
            if (file && isValidCSVFile) {
                reader.readAsText(file);
                reader.onload = (event) => {
                    this.validateCSVDataFormat(event);
                };
                reader.onerror = () => {
                    const message: any = this._translatePipe.get('fileuploads.Something Went Wrong, Please try again after some time');
                    this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
                };
            } else {
                const message: any = this._translatePipe.get('fileuploads.Invalid file format, Please choose valid file format i.e. (.csv)');
                this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
            }
        }
    }

    /**
     ** To Validate the CSV files
     */
    validCSVFile(file): boolean {
        if (file && file.name) {
            const fileDetail = file.name.split('.');
            const fileExtension = fileDetail && fileDetail.length > 0 ? fileDetail[fileDetail.length - 1] : null;
            if (fileExtension === FileUploadConstants.CSV_FILE_EXTENSION) {
                return true;
            }
        }
        return false;
    }

    /**
     * To validate the CSV data columns & rows
     */
    validateCSVDataFormat(event): void {
        const text = event && event.target && event.target.result ? event.target.result : null;
        const allTextLines = text ? text.split(/\r\n|\n/) : null;
        let headers = allTextLines && allTextLines.length ? allTextLines[0] : null;
        this.operatingCountry = '';
        const rowDatas = allTextLines && allTextLines.length > 0 ? allTextLines.filter(row => row !== '') : [];
        if (headers) {
            headers = headers.replace(/["]/g, '');
            headers = headers.split(',');
            const isValidHeaders = this.headerValidations(headers);
            if (isValidHeaders) {
                const templateIdentifier = this.templateInfo ? this.templateInfo.templateIdentifier : '';
                this.isFileUploading = true;
                this.getRecognitionLevelsByParam(templateIdentifier, rowDatas);
            } else {
                const message: any = this._translatePipe.get('fileuploads.Invalid column header, Please input valid column header and try reuploading');
                this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
            }
        }
    }

    /**
     * For header level validations
     */
    headerValidations(headers: string[]): boolean {
        if (headers && headers.length && headers[headers.length - 1] === FileUploadConstants.ERROR_COLUMN) {
            headers.pop();
            this.hasErrorColumn = true;
        } else {
            this.hasErrorColumn = false;
        }
        const headersLength = headers.length;
        if (headers && headersLength > 0) {
            let count = 0;
            let iterate = 0;
            const templateName = this.templateInfo ? this.templateInfo.templateName : '';
            const templateHeaders = this.templateHeaders.find(header => header.templateName === templateName);
            const selectedTemplateHeaders = templateHeaders ? templateHeaders.headers : null;
            const selectedTemplateHeadersLength = selectedTemplateHeaders && selectedTemplateHeaders.length ? selectedTemplateHeaders.length : [];
            if (selectedTemplateHeaders && selectedTemplateHeadersLength > 0 && selectedTemplateHeadersLength === headersLength) {
                for (iterate = 0; iterate < selectedTemplateHeadersLength; iterate++) {
                    if (headers[iterate].toLowerCase().trim() === selectedTemplateHeaders[iterate].toLowerCase().trim()) {
                        count++;
                    }
                }
            }
            if (selectedTemplateHeadersLength === headersLength && headersLength === count
                && selectedTemplateHeadersLength === count) {
                return true;
            }
        }
        return false;
    }

    /**
     * To get the recognition levels from service
     */
    getRecognitionLevelsByParam(param: string, allTextLines): void {
        if (!param) {
            this.isFileUploading = false;
            return;
        }
        this._commonService.getRecognitionLevelsByParam(param).subscribe((response) => {
            if (response && response.length) {
                response.forEach(level => {
                    level.checked = false;
                });
                this.levelsInfo = response;
                this.validateRowData(allTextLines);
            } else {
                this.levelsInfo = [];
                this.validateRowData(allTextLines);
            }
        }, error => {
            const message: any = this._translatePipe.get('fileuploads.Something Went Wrong, Please try again after some time');
            this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
            this.isFileUploading = false;
        });
    }

    /**
     * To Validate the row level datas
     */
    validateRowData(data): void {
        // tslint:disable-next-line: max-line-length
        const templateRules = FileUploadConstants.TEMPLATE_HEADERS.find(template => template.templateName === this.templateInfo.templateName);
        if (!templateRules) {
            this.isFileUploading = false;
            return;
        }
        let isMismatchRowColumnCount = false;
        let errorCount = 0;
        if (data && data.length > 1) {
            for (let i = 1; i < data.length; i++) {
                const rowInfo = data[i].split(',');
                if (this.hasErrorColumn) {
                    rowInfo.pop();
                    data[i] = rowInfo.join(',');
                }
                const errorColumn = [];
                if (rowInfo && rowInfo.length !== templateRules.headers.length) {
                    isMismatchRowColumnCount = true;
                    const message: any = this._translatePipe.get('fileuploads.Please verify the file with data and try reuploading with valid data');
                    this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
                    this.isFileUploading = false;
                    break;
                }
                for (let j = 0; j < templateRules.headers.length; j++) {
                    if (templateRules.validations[templateRules.headers[j]]) {
                        if ((templateRules.validations[templateRules.headers[j]]) &&
                            (typeof (templateRules.validations[templateRules.headers[j]]) === FileUploadConstants.STRING_TYPE)
                        ) {
                            this.validateLookupScenario(templateRules.validations[templateRules.headers[j]],
                                rowInfo[j], templateRules, errorColumn, j);
                        }
                        const isValid = templateRules.validations[templateRules.headers[j]]
                            && typeof (templateRules.validations[templateRules.headers[j]]) !== FileUploadConstants.STRING_TYPE ?
                            (templateRules.validations[templateRules.headers[j]]).test(rowInfo[j]) : true;
                        if (!isValid) {
                            errorColumn.push(templateRules.headers[j] + ' has invalid values');
                        }
                    }
                }
                if (errorColumn && errorColumn.length > 0) {
                    errorCount++;
                    data[i] += ',' + errorColumn.join(':');
                }
            }
            if (errorCount === 0 && !isMismatchRowColumnCount) {
                this.uploadFile();
            } else if (!isMismatchRowColumnCount) {
                this.isFileUploading = false;
                this.errorHeaders = JSON.parse(JSON.stringify(templateRules.headers));
                this.errorRows = JSON.parse(JSON.stringify(data));
                this.prepareDownloadErrorFile();
                const message: any = this._translatePipe.get('fileuploads.Please review the error file and address the errors, and then try reuploading');
                this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
            }
        } else {
            this.isFileUploading = false;
            const message: any = this._translatePipe.get('fileuploads.Please verify the file with data and try reuploading with valid data');
            this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
        }
    }

    /**
     * To Validate the lookup values
     */
    validateLookupScenario(validation, rowValue, templateRules, errorColumn, index): void {
        switch (validation) {
            case COLUMN_VALIDATION.OPERATING_COUNTRY:
                this.validateCountryInfo(rowValue, templateRules, errorColumn, index);
                break;
            case COLUMN_VALIDATION.LEVEL:
                this.validateLevelInfo(rowValue, templateRules, errorColumn, index);
                break;
            default:
                break;
        }
    }

    /**
     * To Validate the levels info
     */
    validateLevelInfo(rowValue, templateRules, errorColumn, index): void {
        if (this.levelsInfo && this.levelsInfo.length) {
            const isValidLevel = this.levelsInfo.find(levelInfo => levelInfo.level.toLowerCase() === rowValue.toLowerCase());
            if (!isValidLevel) {
                errorColumn.push(templateRules.headers[index] + ' has invalid Levels');
            }
        }
    }

    /**
     * To Validate the operating country info
     */
    validateCountryInfo(rowValue, templateRules, errorColumn, index): void {
        if (this.operatingCountriesLookup && this.operatingCountriesLookup.length) {
            const countryInfo = this.operatingCountriesLookup.find(country => country.id === +this.operatingCountryID);
            this.operatingCountry = countryInfo && countryInfo.value ? countryInfo.value.toLowerCase() : '';
            if ((countryInfo && countryInfo.value && countryInfo.value.toLowerCase()) !== (rowValue && rowValue.toLowerCase())) {
                errorColumn.push(templateRules.headers[index] + ' has value mismatch');
            }
            // To verify valid operating country value
            const isValidCountry = this.operatingCountriesLookup.find(country => (country.value.toLowerCase()) === (rowValue && rowValue.toLowerCase()));
            if (!isValidCountry) {
                errorColumn.push(templateRules.headers[index] + ' has invalid Code');
            }
        }
    }

    /**
     * To Upload the files to s3 bucket
     */
    uploadFile() {
        if (this.uploader && this.uploader.queue.length > 0) {
            this.uploadPath = '';
            const formData: FormData = new FormData();
            const fileItem = this.uploader.queue[0];
            const file = fileItem && fileItem.file && fileItem.file.rawFile ? fileItem.file.rawFile : null;
            let uploadFileName = file.name.split('.');
            if (uploadFileName && uploadFileName.length === 2) {
                uploadFileName = uploadFileName[0] + '_' + new Date().getTime() + '.' + uploadFileName[1];
            }
            formData.append('file', file, uploadFileName);
            formData.append('path', FileUploadConstants.BATCH_INITIATATION_PATH);
            formData.append('fileName', uploadFileName);
            this._batchService.uploadFile(formData).subscribe((response) => {
                if (response) {
                    this.uploadPath = response.key;
                    this.getStoreByCountryCode();
                    this.isFileUploading = false;
                }
            }, error => {
                const message: any = this._translatePipe.get('fileuploads.Something Went Wrong, Please try again after some time');
                this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
                this.isFileUploading = false;
            });
        }
    }

    /**
     * To Get stores by country code
     */
    getStoreByCountryCode(): void {
        this._customerService.getStoreInfoFromConfig(this.operatingCountry).subscribe((response) => {
            if (response) {
                const storeInfo = response;
                this.initiateBatchProcess(storeInfo.id, storeInfo.homeCountryId);
            }
        }, error => {
            this.initiateBatchProcess(null, null);
        });
    }

    /**
     * To initiate the batch process
     */
    initiateBatchProcess(id, homeCountryId): void {
        if (this.templateInfo) {
            const userInfo = this._cacheService.get(CacheKey.UserInfo);
            // tslint:disable-next-line: max-line-length
            const operatingCountryInfo = this.operatingCountriesLookup.find(country => country.value.toLowerCase() === this.operatingCountry.toLowerCase());
            const operatingCountryID = operatingCountryInfo ? operatingCountryInfo.id : null;
            const fileUploadInfo = {
                categoryName: this.categoryName,
                categoryIdentifier: this.categoryIdentifier,
                templateName: this.templateInfo.templateName,
                templateExtension: FileUploadConstants.CSV_FILE_EXTENSION,
                templatePath: this.uploadPath,
                uploadSourceSystem: FileUploadSource.CSP,
                destinationSource: DestinationDatabase.RECOGNITION,
                modifiedBy: userInfo ? userInfo.userName : '',
                fileUploadMetaDataModel: {
                    storeId: id,
                    countryId: homeCountryId,
                    operatingCountryId: operatingCountryID,
                    templateIdentifier: this.templateInfo.templateIdentifier
                }
            };
            this._batchService.initiateFileUploadJob(fileUploadInfo).subscribe((response) => {
                if (response && response.success) {
                    this.uploader.queue = [];
                    // tslint:disable-next-line: max-line-length
                    const message: any = this._translatePipe.get('fileuploads.Your file is being processed at the moment, there were no errors. Please check again after some time to know the status of your recent upload via log');
                    this._commonService.createNotification('', message.value, NotificationType.SUCCESS, 0, Constants.FILE_UPLOAD);
                }
            }, error => {
                const message: any = this._translatePipe.get('fileuploads.Something Went Wrong, Please try again after some time');
                this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
            });
        } else {
            const message: any = this._translatePipe.get('fileuploads.Something Went Wrong, Please try again after some time');
                this._commonService.createNotification('', message.value, NotificationType.ERROR, 0, Constants.FILE_UPLOAD);
        }
    }

    /**
     * To Prepare the download error file
     */
    prepareDownloadErrorFile(): void {
        if (this.errorRows && this.errorRows.length > 0 && this.errorHeaders && this.errorHeaders.length > 0) {
            const headers = JSON.parse(JSON.stringify(this.errorHeaders));
            headers.push(FileUploadConstants.ERROR_COLUMN);
            const csvDatas = [];
            for (let i = 1; i < this.errorRows.length; i++) {
                const data = this.errorRows[i].replace(/["]/g, '');
                const dataArray = data ? data.split(',') : [];
                csvDatas.push(dataArray);
            }
            let csvContent = headers.join(',') + '\r\n';
            for (let i = 0, length = csvDatas.length; i < length; i++) {
                csvContent += csvDatas[i].join(',') + '\r\n';
            }
            this.downloadErrorFile(csvContent);
        }
    }

    /**
     * To Download error file
     */
    downloadErrorFile(csvContent): void {
        const filename = this.templateInfo ? this.templateInfo.templateName + '_Error.' + FileUploadConstants.CSV_FILE_EXTENSION : '';
        const blob = new Blob([csvContent], { 'type': 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
        this.showDownloadErrorFile = true;
    }

}



// WEBPACK FOOTER //
// ./src/app/customerservice/components/file-uploads/recognition-file-upload.component.ts